import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiUsers, FiCheck, FiGlobe, FiFilter } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const options = [
  {
    id: 'b',
    icon: FiFilter,
    label: 'Option A',
    title: 'Staff-First, AI Fallback',
    desc: 'Your team answers first. If no one picks up after 15–25 seconds, the call forwards to AI. AI handles it — resolves it, captures details, or auto-forwards the caller to your team with full context.',
    bestFor: 'Most businesses. Your staff answers first — AI catches everything they miss.',
    tag: 'RECOMMENDED',
    tagColor: 'bg-green-100 text-green-800',
    flow: [
      'Customer calls your existing number',
      'Your team answers (as normal)',
      'If no answer after 15–25 sec, call forwards to AI',
      'AI handles the call — resolves it, captures details, or auto-forwards the caller to your team with full context',
    ],
  },
  {
    id: 'a',
    icon: FiGlobe,
    label: 'Option B',
    title: 'AI as Primary Number',
    desc: 'All calls go to AI first. AI answers in under 2 seconds, resolves or auto-forwards to your team. Best for companies that want 24/7 AI-first coverage or dedicated after-hours/overflow lines.',
    bestFor: 'Companies that want 24/7 AI-first coverage on every call, or dedicated after-hours/overflow lines.',
    tag: 'Alternative',
    tagColor: 'bg-slate-100 text-slate-700',
    flow: [
      'Customer calls your AI number directly',
      'AI answers in <2 seconds',
      'AI resolves or auto-forwards to your team',
      'Your team gets full context and transcript before picking up',
    ],
  },
];

const DeploymentModel = () => {
  const [active, setActive] = useState('b');
  const selected = options.find((o) => o.id === active);

  return (
    <section className="py-12 md:py-16 bg-slate-50 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">How Calls Reach Your AI Agent</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Your AI agent gets a dedicated phone number. You keep your existing number and phone system — we just add a forwarding rule so AI catches what your team misses.
          </p>
        </motion.div>

        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-xl border border-slate-200 p-1 gap-1 shadow-sm">
            {options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setActive(opt.id)}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  active === opt.id ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {opt.label}: {opt.title}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start max-w-5xl mx-auto">
          <motion.div
            key={active}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <SafeIcon icon={selected.icon} className="w-5 h-5 text-slate-700" />
              </div>
              <div>
                <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold mb-1 ${selected.tagColor}`}>
                  {selected.tag}
                </div>
                <h3 className="text-lg font-bold text-slate-900">{selected.title}</h3>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              {selected.flow.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-700 text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </div>
                  <div className="flex-1 text-sm text-slate-700 font-medium">{step}</div>
                </div>
              ))}
            </div>
            <div className="bg-green-50 rounded-xl p-4 border border-green-100">
              <div className="text-xs font-bold text-green-800 uppercase tracking-wider mb-1">Best for</div>
              <p className="text-sm text-green-900">{selected.bestFor}</p>
            </div>
          </motion.div>

          <div className="space-y-4">
            <div className="bg-slate-900 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-2 mb-4">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-green-400" />
                <h4 className="font-bold text-sm uppercase tracking-wider text-slate-300">In Both Options</h4>
              </div>
              <div className="space-y-3">
                {[
                  'AI picks up in under 2 seconds — every time',
                  'Speaks 32+ languages with automatic detection and mid-conversation switching',
                  'Auto-forwards callers to your team when needed — caller stays on the line',
                  'Your team receives full transcript and context before they even pick up',
                  'All calls recorded with full transcripts available in your dashboard',
                  'Your existing phone system requires zero changes',
                  'No caller is left without a response path',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-400" />
                    </div>
                    <span className="text-slate-300 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiUsers} className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-bold text-slate-700">During Your 14-Day Pilot</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                We'll help you determine which deployment model fits your operation — and handle all the configuration on our end. You just need to point calls at the AI number.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeploymentModel;